// Sample PrintHQ Cloud data for demo mode (no Supabase configured): a Maker
// trial with two computers, a few backed-up files and some activity. Used
// only so the screens can be seen; nothing here reaches a server.
import type { ActivityRow, CloudNotification, Device, Entitlement, Flags, Plans, Quota, RestorePoint, StorageBreakdown, SyncCategories, SyncStatusRow } from './cloud'

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
export const isDemo = () => !url || url.includes('your_')

const now = () => Date.now()
const iso = (msAgo: number) => new Date(now() - msAgo).toISOString()
const H = 3600_000
const D = 24 * H
// ?cloud=available|trial|expired|paid|canceled|past_due picks the demo state.
const mode = () => (typeof location === 'undefined' ? 'trial' : new URLSearchParams(location.search).get('cloud') ?? 'trial')
let started = false

export function entitlement(): Entitlement {
  const m = started ? 'trial' : mode()
  const trialOn = m === 'trial'
  const paid = ['paid', 'canceled', 'past_due'].includes(m)
  return {
    version: 1,
    state: m === 'available' ? 'trial_available' : trialOn ? 'trial_active' : m === 'expired' ? 'trial_expired'
      : m === 'canceled' ? 'subscription_canceled' : m === 'past_due' ? 'subscription_past_due' : 'subscription_active',
    entitled: trialOn || paid,
    plan: trialOn || paid ? 'maker' : null,
    source: trialOn ? 'trial' : paid ? 'subscription' : null,
    access_until: trialOn ? iso(-(4 * D + 5 * H)) : paid ? iso(-18 * D) : null,
    reason: m === 'past_due' ? 'payment_failed' : m === 'expired' ? 'expired' : null,
    trial: {
      eligibility: 'eligible',
      available: m === 'available',
      can_start: m === 'available',
      activated_at: m === 'available' ? null : iso(3 * D - 5 * H),
      expires_at: m === 'available' ? null : trialOn ? iso(-(4 * D + 5 * H)) : iso(2 * D),
      duration_hours: 168,
      consumed: m !== 'available',
    },
    subscription: paid ? { id: 'sub_demo', status: m === 'past_due' ? 'past_due' : 'active', plan: 'maker', interval: 'year', current_period_end: iso(-18 * D), cancel_at_period_end: m === 'canceled', canceled_at: null, ended_at: null } : null,
    grant: null,
    other_plan: null,
    revocation_version: 0,
    limits: { storage_gb: trialOn ? 5 : paid ? 20 : 1, uploads_allowed: trialOn || paid, scenes_limit: trialOn ? 25 : null, scenes_period: trialOn ? 'trial' : paid ? 'month' : null },
    retention: { policy: 'indefinite' },
    computed_at: new Date().toISOString(),
  }
}

export function startTrial() {
  started = true
  return Promise.resolve({ ok: true, code: 'started', entitlement: entitlement() })
}

export function flags(): Flags {
  return { cloud_dashboard: true, cloud_pricing_page: true, trial_eligibility: true, trial_activation: true, vault_trial_enforcement: true, legacy_nav: true, storage_entitlements: true, planner: true, planner_google: true, planner_feeds: true, planner_alerts: true, kairo: true, kairo_credit_packs: true, kairo_contributions: true }
}

export function plans(): Plans {
  return {
    maker: {
      month: { price_id: 'price_demo_m', amount: 500, currency: 'usd', interval: 'month' },
      year: { price_id: 'price_demo_y', amount: 5000, currency: 'usd', interval: 'year' },
      yearly_saving_pct: 17,
    },
  }
}

export function devices(): Device[] {
  return [
    {
      id: 'd1', kind: 'desktop', device_name: 'Print Room Mac', platform: 'macos', app_version: '1.2.0', registered_at: iso(40 * D),
      last_seen_at: iso(12 * 60_000), last_verified_at: iso(3 * H), last_sync_at: iso(14 * 60_000), auth_status: 'active', revoked_reason: null,
      capabilities: { sync: true }, sync_device_id: 'sd1',
      sync: { sync_enabled: true, synced_models: 118, pending: 2, failed: 0, conflicts: 0, last_error: null, last_attempt_at: iso(14 * 60_000), reported_at: iso(14 * 60_000), retry_requested_at: null, retry_acknowledged_at: null },
    },
    {
      id: 'd2', kind: 'desktop', device_name: 'Workshop PC', platform: 'windows', app_version: '1.1.3', registered_at: iso(90 * D),
      last_seen_at: iso(2 * D), last_verified_at: iso(2 * D), last_sync_at: iso(2 * D), auth_status: 'active', revoked_reason: null,
      capabilities: {}, sync_device_id: 'sd2',
      sync: { sync_enabled: true, synced_models: 64, pending: 0, failed: 1, conflicts: 1, last_error: 'The file changed while it was uploading', last_attempt_at: iso(2 * D), reported_at: iso(2 * D), retry_requested_at: null, retry_acknowledged_at: null },
    },
  ]
}

export function syncStatus(): SyncStatusRow[] {
  return [{
    device_id: 'sd2', activation_id: 'd2', last_success_at: iso(2 * D), reported_at: iso(2 * D),
    failed_items: [{ object_id: 'o9', model_id: 'm3', file_name: 'Gear Housing v3.3mf', code: 'upload', message: 'The file changed while it was uploading', at: iso(2 * D) }],
    conflicts: [{ object_id: 'o4', model_id: 'm2', file_name: 'Clip Mount.stl', remote_version: 3, detected_at: iso(2 * D + H) }],
  }]
}

export function syncCategories(): SyncCategories {
  return { model_metadata: true, model_files: true, thumbnails: true, filament: true }
}

export function quota(): Quota {
  return { tier: 'trial', limit_bytes: 5 * 1024 ** 3, used_bytes: 2.3 * 1024 ** 3, vault_used_bytes: 2.1 * 1024 ** 3, files_used_bytes: 0.2 * 1024 ** 3, versions_retained: 10, uploads_allowed: true }
}

export function breakdown(): StorageBreakdown {
  const G = 1024 ** 3
  return {
    vault: {
      models: { bytes: 1.4 * G, files: 212 },
      print_files: { bytes: 0.5 * G, files: 61 },
      images: { bytes: 0.15 * G, files: 40 },
      archives: { bytes: 0.05 * G, files: 3 },
    },
    web_files_bytes: 0.2 * G,
  }
}

export function restorePoints(): RestorePoint[] {
  const row = (i: number, name: string, model: string, v: number, cur: boolean, ago: number, dev: string): RestorePoint => ({
    object_id: `o${i}`, version: v, is_current: cur, file_name: name, file_ext: name.split('.').pop() ?? null, size_bytes: 1.2e6 + i * 3.1e5,
    content_hash: 'blake3:' + 'ab'.repeat(32), created_at: iso(ago), device_id: 'sd1', device_name: dev, model_id: `m${i}`, model_name: model, folder_path: 'Functional',
  })
  return [
    row(1, 'Gear Housing.3mf', 'Gear Housing', 3, true, 2 * H, 'Print Room Mac'),
    row(1, 'Gear Housing.3mf', 'Gear Housing', 2, false, 26 * H, 'Workshop PC'),
    row(2, 'Clip Mount.stl', 'Clip Mount', 1, true, 3 * D, 'Print Room Mac'),
    row(1, 'Gear Housing.3mf', 'Gear Housing', 1, false, 6 * D, 'Print Room Mac'),
  ]
}

export function activity(): ActivityRow[] {
  return [
    { id: 5, device_id: 'sd1', device_name: 'Print Room Mac', action: 'upload', object_id: 'o1', model_id: 'm1', detail: { version: 3, size_bytes: 2_400_000, file_name: 'Gear Housing.3mf' }, created_at: iso(2 * H) },
    { id: 4, device_id: null, device_name: null, action: 'share_create', object_id: 'o2', model_id: 'm2', detail: { item_count: 1 }, created_at: iso(20 * H) },
    { id: 3, device_id: 'sd2', device_name: 'Workshop PC', action: 'upload', object_id: 'o1', model_id: 'm1', detail: { version: 2, size_bytes: 2_300_000, file_name: 'Gear Housing.3mf' }, created_at: iso(26 * H) },
    { id: 2, device_id: 'sd1', device_name: 'Print Room Mac', action: 'delete', object_id: 'o7', model_id: 'm7', detail: null, created_at: iso(4 * D) },
  ]
}

export function notifications(): CloudNotification[] {
  return [
    { id: 'n1', kind: 'trial_activated', title: 'Your 7-day PrintHQ Vault trial has started', body: 'Everything in PrintHQ Maker is yours for a week. No card needed.', link: '/trial', severity: 'success', read_at: null, created_at: iso(3 * D) },
    { id: 'n2', kind: 'new_device', title: 'A new computer signed in to PrintHQ Vault', body: 'Workshop PC (windows) was activated on your account.', link: '/devices', severity: 'info', read_at: iso(2 * D), created_at: iso(3 * D) },
  ]
}

export function notificationPrefs(): Record<string, boolean> {
  return {
    trial_activated: true, trial_3_days: true, trial_1_day: true, trial_today: true, trial_expired: true,
    billing_subscribed: true, billing_payment_failed: true, billing_canceled: true,
    sync_failed: false, storage_nearly_full: true, new_device: true, device_revoked: true,
  }
}
